import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { ActivityFeed } from '../components/ActivityFeed';
import { showSuccess, showError, showWarning, Alert } from '../lib/alerts';

interface LoginPageProps {
  supabase: SupabaseClient;
  session: Session | null;
}

export default function LoginPage({ supabase, session }: LoginPageProps) {
  const brandLogoSrc = '/founderpulselogo.png';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'signup'>(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setMode(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  }, [searchParams]);

  const isSignup = mode === 'signup';

  const signInCopy = useMemo(
    () => (isSignup ? 'Create your FounderPulse account' : 'Access your workspace analytics'),
    [isSignup]
  );

  async function handleSignIn() {
    if (!email || !password) {
      showWarning('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        showError(error.message);
      } else {
        const signedInUser = data.user;
        if (signedInUser) {
          await supabase.from('users').upsert(
            {
              id: signedInUser.id,
              email: signedInUser.email ?? email,
              full_name: signedInUser.user_metadata?.full_name ?? null,
            },
            { onConflict: 'id' }
          );
        }
        showSuccess('Welcome back!');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Unexpected error. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp() {
    if (!email || !password) {
      showWarning('Please fill all fields');
      return;
    }
    if (password.length < 6) {
      showWarning('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName || undefined } },
      });
      if (error) {
        showError(error.message);
      } else {
        const signedUpUser = data.user;
        if (signedUpUser) {
          await supabase.from('users').upsert(
            {
              id: signedUpUser.id,
              email: signedUpUser.email ?? email,
              full_name: fullName || signedUpUser.user_metadata?.full_name || null,
            },
            { onConflict: 'id' }
          );
        }
        Alert.fire({ icon: 'success', title: 'Account Created!', html: `Check <b>${email}</b> to confirm before signing in.`, confirmButtonText: 'Got it' });
      }
    } catch (err) {
      showError('Sign up failed. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="split-screen login-screen">
      <div className="split-screen__left login-hero">
        <div className="login-hero__top">
          <div className="brand-lockup brand-lockup--login">
            <div className="brand-mark">
              <img className="brand-mark__image" src={brandLogoSrc} alt="FounderPulse logo" />
            </div>
            <div className="brand-copy">
              <span>FounderPulse</span>
            </div>
          </div>
          <button
            type="button"
            className="button button--ghost button--mono login-home-button"
            onClick={() => navigate('/')}
            disabled={loading}
          >
            Home
          </button>
        </div>

        <div className="login-feed-wrap">
          <ActivityFeed />
        </div>

        <div className="login-stats">
          <div className="login-stat">
            <div className="login-stat__value">Real-time</div>
            <div className="login-stat__label">Activity Tracking</div>
          </div>
          <div className="login-stat">
            <div className="login-stat__value">Team</div>
            <div className="login-stat__label">Analytics & Insights</div>
          </div>
          <div className="login-stat">
            <div className="login-stat__value">VS Code</div>
            <div className="login-stat__label">Extension Built-in</div>
          </div>
        </div>

        <p className="login-quote">"Stop guessing who&apos;s doing the work."</p>

        <footer className="login-footer">
          <p>
            Built by{' '}
            <a href="https://mathewjolly.vercel.app" target="_blank" rel="noopener noreferrer">
              Mathew Jolly
            </a>
          </p>
        </footer>
      </div>

      <div className="split-screen__right login-panel">
        <div className="auth-panel">
          <div className="state-pill state-pill--compact">FounderPulse</div>
          <h1>{isSignup ? 'Sign Up' : 'Sign In'}</h1>
          <p className="panel-subtitle">{signInCopy}</p>

          <div className="auth-form">
            {isSignup ? (
              <label className="field">
                <span>Full name</span>
                <input value={fullName} onChange={(event) => setFullName(event.target.value)} type="text" required disabled={loading} />
              </label>
            ) : null}

            <label className="field">
              <span>Email</span>
              <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required disabled={loading} />
            </label>

            <label className="field">
              <span>Password</span>
              <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                <input 
                  value={password} 
                  onChange={(event) => setPassword(event.target.value)} 
                  type={showPassword ? "text" : "password"} 
                  required 
                  disabled={loading}
                  style={{ width: '100%', paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    fontSize: '1rem',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.7,
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </label>

            <div>
              <button type="button" className="button button--solid button--full button--mono" onClick={isSignup ? handleSignUp : handleSignIn} disabled={loading}>
                {loading ? 'Loading...' : isSignup ? 'Sign Up →' : 'Sign In →'}
              </button>
            </div>

            <div className="divider-row">
              <span>or</span>
            </div>

            <button
              type="button"
              className="button button--auth-switch button--mono button--full"
              onClick={() => {
                const nextMode = isSignup ? 'signin' : 'signup';
                setMode(nextMode);
                navigate(nextMode === 'signup' ? '/login?mode=signup' : '/login', { replace: true });
              }}
              disabled={loading}
            >
              {isSignup ? 'Back to Sign In' : 'Create Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
